import { forwardRef, useImperativeHandle } from "react";
import { Box, Space, Text, Title } from "@mantine/core";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { Product } from "../../../types/interfaces";
import { ImprintTypeChoices } from "../../../types/constants";
import { ImprintType } from "../../../types/enums";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";

const ENABLE_LINK_TO_EXISTING_ORDER = false;

type ImprintChoiceFormData = {
  imprintType: ImprintType;
  linkToExistingOrder: boolean;
  linkedOrderId?: string;
};

type CreateRemovableItemFullDentureImprintChoiceProps = {
  product: Product;
};

export interface ImprintChoiceRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemFullDentureImprintChoice = forwardRef<
  ImprintChoiceRef,
  CreateRemovableItemFullDentureImprintChoiceProps
>(({ product }, ref) => {
  const newRemovableItem = product.removableItem?.[0];

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<ImprintChoiceFormData>({
    defaultValues: {
      imprintType:
        newRemovableItem?.imprintType ?? ImprintType.EDENTULOUS_ARCH_RDV1,
      linkToExistingOrder: newRemovableItem?.linkToExistingOrder ?? false,
      linkedOrderId: newRemovableItem?.linkedOrderId ?? undefined,
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM, {
    refetchQueries: ["getOrderByUniqueAttribute"],
    awaitRefetchQueries: true,
  });

  const imprintType = watch("imprintType");
  const linkToExistingOrder = watch("linkToExistingOrder");
  const linkedOrderId = watch("linkedOrderId");

  const saveData = async (): Promise<void> => {
    try {
      if (!newRemovableItem || !imprintType) {
        return;
      }

      const rdvNumber = imprintType === ImprintType.WAX_BITE_RDV2 ? 2 : 1;
      await updateRemovableItem({
        variables: {
          where: {
            id: newRemovableItem.id,
          },
          data: {
            imprintType: { set: imprintType },
            rdvNumber: { set: rdvNumber },
            linkToExistingOrder: { set: linkToExistingOrder },
            linkedOrderId: linkedOrderId ? { set: linkedOrderId } : undefined,
          },
        },
      });
    } catch (error) {
      console.error("Error updating removable item:", error);
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!newRemovableItem) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  return (
    <Box>
      <Title order={3}> Vous venez de prendre une empreinte de :</Title>
      <Space h="md" />
      <Controller
        name="imprintType"
        rules={{ required: true }}
        control={control}
        render={({ field }) => (
          <ButtonsSelect
            data={ImprintTypeChoices}
            value={field.value}
            onChange={value => field.onChange(value)}
            numberOfColumns={1}
            align="left"
          />
        )}
      />

      {errors?.imprintType?.type === "required" && (
        <Text size="sm" style={{ color: "red" }}>
          Veuillez sélectionner le type d'empreinte
        </Text>
      )}

      <Space h="xl" />

      {ENABLE_LINK_TO_EXISTING_ORDER && (
        <>
          <Title order={4}>
            Souhaitez-vous lier cette commande à un dossier déjà existant ?
          </Title>

          <Space h="md" />

          <Controller
            name="linkToExistingOrder"
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={[
                  { value: true, label: "Oui" },
                  { value: false, label: "Non" },
                ]}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />

          {linkToExistingOrder && (
            <>
              <Space h="md" />
              <Text size="sm" c="dimmed">
                Fonctionnalité de recherche de commande à implémenter (Phase
                ultérieure)
              </Text>
            </>
          )}

          <Space h="xl" />
        </>
      )}
    </Box>
  );
});

CreateRemovableItemFullDentureImprintChoice.displayName =
  "CreateRemovableItemFullDentureImprintChoice";
